<template>
  <div
    class="whale-empty-container"
    :class="[
      `size-${size}`,
      {
        'is-compact': compact,
        'is-bordered': bordered,
      },
    ]"
    role="status"
    aria-live="polite"
  >
    <div class="whale-empty-visual">
      <div class="whale-empty-aura" aria-hidden="true"></div>
      <div class="whale-empty-illustration-wrap">
        <EmptyIllustration :variant="resolvedVariant" />
        <span class="whale-sparkle sparkle-1" aria-hidden="true">✦</span>
        <span class="whale-sparkle sparkle-2" aria-hidden="true">✦</span>
      </div>
      <div class="whale-empty-shadow" aria-hidden="true"></div>
    </div>

    <div class="whale-empty-content">
      <h4 v-if="title" class="whale-empty-title">{{ title }}</h4>
      <p v-if="description" class="whale-empty-desc">{{ description }}</p>
      <slot />
    </div>

    <div v-if="$slots.action || actionText || secondaryActionText" class="whale-empty-actions">
      <slot name="action">
        <button
          v-if="secondaryActionText"
          type="button"
          class="btn-empty-secondary"
          @click="$emit('secondary-action')"
        >
          {{ secondaryActionText }}
        </button>
        <button
          v-if="actionText"
          type="button"
          class="btn-empty-primary"
          @click="$emit('action')"
        >
          <component :is="actionIcon" v-if="actionIcon" class="btn-icon" :size="15" />
          <span>{{ actionText }}</span>
        </button>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import EmptyIllustration from '@/components/common/EmptyIllustration.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '暂无相关数据',
  },
  size: {
    type: String,
    default: 'medium',
    validator: val => ['small', 'medium', 'large'].includes(val),
  },
  variant: {
    type: String,
    default: 'default',
    validator: val => ['default', 'materials', 'templates', 'palette', 'search'].includes(val),
  },
  actionText: {
    type: String,
    default: '',
  },
  actionIcon: {
    type: Object,
    default: null,
  },
  secondaryActionText: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
  bordered: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['action', 'secondary-action']);

const resolvedVariant = computed(() => props.variant);
</script>

<style scoped>
.whale-empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px 32px;
  text-align: center;
  user-select: none;
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
}

.whale-empty-container.is-compact {
  padding: 20px 16px 16px;
}

.whale-empty-container.is-bordered {
  background: #ffffff;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
}

.whale-empty-visual {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.whale-empty-aura {
  position: absolute;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.1) 0%, rgba(59, 130, 246, 0.04) 45%, rgba(255, 255, 255, 0) 72%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}

.whale-empty-illustration-wrap {
  position: relative;
  z-index: 1;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: empty-float 4.5s ease-in-out infinite;
}

.whale-empty-container:hover .whale-empty-illustration-wrap {
  transform: translateY(-4px) scale(1.02);
}

.size-small .whale-empty-illustration-wrap {
  width: 120px;
}

.size-medium .whale-empty-illustration-wrap {
  width: 148px;
}

.size-large .whale-empty-illustration-wrap {
  width: 176px;
}

.whale-sparkle {
  position: absolute;
  color: #a78bfa;
  font-size: 10px;
  line-height: 1;
  pointer-events: none;
  animation: sparkle-pulse 2.8s ease-in-out infinite;
}

.sparkle-1 {
  top: 6px;
  right: 4px;
  animation-delay: 0.4s;
}

.sparkle-2 {
  bottom: 14px;
  left: 2px;
  font-size: 8px;
  color: #93c5fd;
  animation-delay: 1.1s;
}

@keyframes empty-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes sparkle-pulse {
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.1);
  }
}

.whale-empty-shadow {
  width: 96px;
  height: 10px;
  background: radial-gradient(ellipse at center, rgba(124, 58, 237, 0.14) 0%, rgba(15, 23, 42, 0) 72%);
  border-radius: 50%;
  margin-top: 6px;
  z-index: 0;
  transition: all 0.3s ease;
}

.size-small .whale-empty-shadow {
  width: 72px;
  height: 7px;
}

.size-large .whale-empty-shadow {
  width: 112px;
  height: 12px;
}

.whale-empty-container:hover .whale-empty-shadow {
  transform: scale(0.9);
  opacity: 0.75;
}

.whale-empty-content {
  position: relative;
  z-index: 1;
  max-width: 380px;
}

.whale-empty-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.size-large .whale-empty-title {
  font-size: 17px;
}

.size-small .whale-empty-title {
  font-size: 13.5px;
}

.whale-empty-desc {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.65;
}

.size-small .whale-empty-desc {
  font-size: 12px;
}

.whale-empty-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  z-index: 1;
}

.btn-empty-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #7c3aed 0%, #9333ea 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.22);
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.size-large .btn-empty-primary {
  height: 40px;
  padding: 0 24px;
  font-size: 13.5px;
}

.btn-empty-primary:hover {
  background: linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.28);
}

.btn-empty-primary:active {
  transform: translateY(0);
}

.btn-empty-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-empty-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}
</style>
